import React, { useMemo, useRef, useState } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useGlobalState } from "../../global/context/global-context";
import { useRoutedNavigation } from "../../hooks/useRoutedNavigation";
import { getInstitutionalBanners, IInstitutionalBannerAction, isChamber } from "../institutional-banners";
import * as S from "./institutional-banner-slide.styles";

const SLIDE_DURATION_MS = 8000;
const SWIPE_THRESHOLD_PX = 50;

const isKeyboardFocus = (element: Element): boolean => {
  try {
    return element.matches(":focus-visible");
  } catch {
    return true;
  }
};

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function BannerAction({ action, variant }: { action: IInstitutionalBannerAction; variant: "primary" | "secondary" }) {
  const { buildPath } = useRoutedNavigation();
  const content = (
    <>
      {action.label}
      {variant === "primary" && <ArrowForwardIcon />}
    </>
  );

  // Ouvidoria e SIC são sistemas externos: abrem em nova aba
  if (/^https?:\/\//i.test(action.url)) {
    return (
      <S.ActionAnchor $variant={variant} href={action.url} target="_blank" rel="noopener noreferrer">
        {content}
        <S.VisuallyHidden> (abre em nova aba)</S.VisuallyHidden>
      </S.ActionAnchor>
    );
  }

  return (
    <S.ActionRouterLink $variant={variant} to={buildPath(action.url)}>
      {content}
    </S.ActionRouterLink>
  );
}

/**
 * Carrossel institucional do topo da home, exibido quando a entidade não tem banners cadastrados no APM.
 * Conteúdo de prefeitura ou câmara conforme o tipo da entidade, nas cores do portal.
 */
export default function InstitutionalBannerSlide() {
  const { applicationData } = useGlobalState();
  const entity = applicationData?.entity;
  const banners = useMemo(() => getInstitutionalBanners(entity), [entity]);
  const entityLabel = entity?.nickName || (isChamber(entity) ? "Câmara Municipal" : "Prefeitura Municipal");

  const [active, setActive] = useState(0);
  // Autoplay desligado para quem pede menos movimento; o usuário pode iniciar pelo botão
  const [userPaused, setUserPaused] = useState(prefersReducedMotion);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const total = banners.length;
  const paused = userPaused || hovered || focused;
  const goTo = (index: number) => setActive((index + total) % total);

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;
    goTo(deltaX < 0 ? active + 1 : active - 1);
  };

  if (total === 0) return null;

  return (
    <S.Stage
      aria-roledescription="carrossel"
      aria-label="Destaques institucionais"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Pausa só com foco de teclado (APG); clique do mouse em um controle não deve travar o carrossel
      onFocus={(event) => setFocused(isKeyboardFocus(event.target))}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
      }}
      onTouchStart={(event) => (touchStartX.current = event.touches[0].clientX)}
      onTouchEnd={handleTouchEnd}
    >
      <S.Rings viewBox="0 0 720 720" aria-hidden>
        {[120, 200, 280, 360].map((radius) => (
          <circle key={radius} cx="360" cy="360" r={radius - 1} fill="none" stroke="currentColor" strokeWidth="1.5" />
        ))}
      </S.Rings>

      <S.Slides aria-live={userPaused ? "polite" : "off"}>
        {banners.map((banner, index) => {
          const BannerIcon = banner.icon;
          return (
            <S.Slide
              key={banner.key}
              $active={index === active}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} de ${total}: ${banner.tab}`}
            >
              <S.Content>
                <S.Eyebrow>
                  <span>
                    <BannerIcon />
                  </span>
                  {banner.eyebrow}
                </S.Eyebrow>
                <S.Title>{banner.title}</S.Title>
                <S.Description>{banner.description}</S.Description>
                <S.Actions>
                  <BannerAction action={banner.primaryAction} variant="primary" />
                  {banner.secondaryAction && <BannerAction action={banner.secondaryAction} variant="secondary" />}
                </S.Actions>
              </S.Content>

              <S.Visual>
                <S.Panel>
                  <S.PanelHeader>
                    <S.PanelIcon>
                      <BannerIcon />
                    </S.PanelIcon>
                    <S.PanelHeading>
                      <strong>{banner.panelTitle}</strong>
                      <span>{entityLabel}</span>
                    </S.PanelHeading>
                  </S.PanelHeader>
                  <S.PanelList>
                    {banner.highlights.map(({ label, icon: HighlightIcon }) => (
                      <li key={label}>
                        <S.PanelItemIcon>
                          <HighlightIcon />
                        </S.PanelItemIcon>
                        {label}
                      </li>
                    ))}
                  </S.PanelList>
                </S.Panel>
                <S.LegalBadge>
                  <VerifiedIcon />
                  {banner.legalNote}
                </S.LegalBadge>
              </S.Visual>
            </S.Slide>
          );
        })}
      </S.Slides>

      <S.Controls>
        <S.Tabs>
          {banners.map((banner, index) => (
            <S.Tab
              key={banner.key}
              type="button"
              $active={index === active}
              aria-label={`Exibir destaque ${index + 1} de ${total}: ${banner.tab}`}
              aria-current={index === active ? "true" : undefined}
              onClick={() => goTo(index)}
            >
              <span>{banner.tab}</span>
              <S.TabTrack $active={index === active}>
                {index === active && total > 1 && (
                  // key força reiniciar a animação a cada troca de slide
                  <S.TabFill
                    key={`${active}-${userPaused}`}
                    $duration={SLIDE_DURATION_MS}
                    $paused={paused}
                    $stopped={userPaused}
                    onAnimationEnd={() => goTo(active + 1)}
                  />
                )}
              </S.TabTrack>
            </S.Tab>
          ))}
        </S.Tabs>

        {total > 1 && (
          <S.NavButtons>
            <S.NavButton type="button" aria-label="Destaque anterior" onClick={() => goTo(active - 1)}>
              <ChevronLeftIcon />
            </S.NavButton>
            <S.NavButton
              type="button"
              aria-label={userPaused ? "Retomar troca automática" : "Pausar troca automática"}
              onClick={() => setUserPaused((current) => !current)}
            >
              {userPaused ? <PlayArrowIcon /> : <PauseIcon />}
            </S.NavButton>
            <S.NavButton type="button" aria-label="Próximo destaque" onClick={() => goTo(active + 1)}>
              <ChevronRightIcon />
            </S.NavButton>
          </S.NavButtons>
        )}
      </S.Controls>
    </S.Stage>
  );
}
